import { CrmFieldService, StandardIds, SystemFieldIds, StandardExternalIds } from '@galaxy/crm/services/field-service';
import { CommonModule } from '@angular/common';
import { Component, Inject, Input, computed, effect, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';
import { ActivityInterface, CRMChangeLogApiService, CrmObjectChangeLogInterface } from '@vendasta/crm';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';

import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { SalesSdk, SalespersonService, Salesperson } from '@vendasta/sales';
import { EMPTY, combineLatest, of, switchMap, take } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  CrmInjectionToken,
  CrmDependencies,
  SimplifiedUser,
  ObjectType,
  RecordChangeType,
} from '../../../tokens-and-interfaces';
import { TranslationModule } from '../../../i18n/translation-module';
import { AVATAR_WIDTH } from '../../../constants';
import { TranslateForCrmObjectService } from '../../../i18n';

interface EffectResult {
  changeLog: CrmObjectChangeLogInterface;
  changeLogSalesPerson: Salesperson | null;
  changeLogUser: SimplifiedUser[];
  changeLogSalespersonId: string | null;
}

const RecordChangeIcons = {
  Default: 'business',
  Opportunity: 'monetization_on',
};

@Component({
  selector: 'crm-activity-record-change',
  templateUrl: './record-change.component.html',
  styleUrls: ['./record-change.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, TranslateModule, TranslationModule, MatCardModule, GalaxyPipesModule, GalaxyAvatarModule],
})
export class CrmActivityRecordChangeComponent {
  AVATAR_WIDTH = AVATAR_WIDTH;
  dateFormat = DateFormat.medium;

  #activity = signal<ActivityInterface>({});
  private readonly namespace = toSignal(this.config.namespace$);

  private _showAvatar = true;

  isReady = signal<boolean>(false);

  @Input() set activity(activity: ActivityInterface) {
    this.#activity.set(activity);
  }

  get activity(): ActivityInterface {
    return this.#activity();
  }

  get showAvatar() {
    return this._showAvatar;
  }

  objectType = computed(() => {
    const activity = this.#activity();
    if (activity.associations === undefined || activity.associations.length === 0) {
      console.warn('No associations found for activity. Could not deduce object type.', this.#activity().crmObjectId);
      return undefined;
    }
    return activity.associations[0].crmObjectType as ObjectType;
  });
  objectType$ = toObservable(this.objectType);

  private readonly salespersonUnknownTranslation = toSignal(
    this.objectType$.pipe(
      switchMap((objectType) => {
        return this.translateForCrmObjectService.getTranslationForCrmObject(
          objectType || 'Contact',
          'RECORD_CHANGE_ACTIVITY.IMPORTANT_FIELD_UPDATED_SALESPERSON_UNKNOWN',
        );
      }),
    ),
  );
  private readonly salespersonUpdatedTranslation = toSignal(
    this.objectType$.pipe(
      switchMap((objectType) => {
        return this.translateForCrmObjectService.getTranslationForCrmObject(
          objectType || 'Contact',
          'RECORD_CHANGE_ACTIVITY.IMPORTANT_FIELD_UPDATED_SALESPERSON',
        );
      }),
    ),
  );
  private readonly salespersonNotAssignedTranslation = toSignal(
    this.objectType$.pipe(
      switchMap((objectType) => {
        return this.translateForCrmObjectService.getTranslationForCrmObject(
          objectType || 'Contact',
          'RECORD_CHANGE_ACTIVITY.IMPORTANT_FIELD_UPDATED_SALESPERSON_NOT_ASSIGNED',
        );
      }),
    ),
  );
  private readonly updatedByUserTranslation = toSignal(
    this.objectType$.pipe(
      switchMap((objectType) => {
        return this.translateForCrmObjectService.getTranslationForCrmObject(
          objectType || 'Contact',
          'RECORD_CHANGE_ACTIVITY.IMPORTANT_FIELD_UPDATED',
          { updatedField: `Salesperson` },
        );
      }),
    ),
  );
  private readonly createdViaTranslation = toSignal(
    this.objectType$.pipe(
      switchMap((objectType) => {
        return this.translateForCrmObjectService.getTranslationForCrmObject(
          objectType || 'Contact',
          'RECORD_CHANGE_ACTIVITY.CREATED_VIA',
        );
      }),
    ),
  );
  private readonly createdTranslation = toSignal(
    this.objectType$.pipe(
      switchMap((objectType) => {
        return this.translateForCrmObjectService.getTranslationForCrmObject(
          objectType || 'Contact',
          'RECORD_CHANGE_ACTIVITY.CREATED',
        );
      }),
    ),
  );

  recordChangeType = computed(() => {
    return this.fieldService.getFieldValueFromCrmObject(this.#activity(), StandardExternalIds.ActivityRecordChangeType)
      ?.stringValue as RecordChangeType;
  });

  icon = computed(() => {
    const objectType = this.objectType();
    if (objectType === 'Opportunity') {
      return RecordChangeIcons.Opportunity;
    }
    return RecordChangeIcons.Default;
  });

  sourceName = computed(() => {
    return this.fieldService.getFieldValueFromCrmObject(this.#activity(), StandardIds.ActivitySourceName)?.stringValue;
  });

  cardInfo = computed(() => {
    return {
      isReady: this.isReady(),
      headerText: this.headerText(),
      contentText: this.contentText(),
      effectiveDate: this.effectiveDate(),
      isUpdatedRecordChange: this.isUpdatedRecordChange(),
    };
  });

  headerText = computed(() => {
    const effectResult = this.effectResult();

    let createdTranslation: string | undefined = '';
    switch (this.recordChangeType()) {
      case 'CREATED':
        createdTranslation = this.createdTranslation();
        break;
      case 'IMPORTANT_FIELD_UPDATED':
        createdTranslation = this.updatedByUserTranslation();
        break;
    }
    const computedObjectType = this.objectType();
    if (computedObjectType === undefined) {
      return '';
    }
    const sourceName = this.sourceName();
    if (sourceName !== undefined) {
      createdTranslation = this.createdViaTranslation();
    }
    if (sourceName !== undefined) {
      return `${createdTranslation} ${sourceName}`;
    }

    if (this.recordChangeType() === 'IMPORTANT_FIELD_UPDATED') {
      return `${this.updatedByUserTranslation()} ${effectResult?.changeLogUser[0].displayName ?? ''}`.trim();
    }
    return createdTranslation;
  });

  effectiveDate = computed(() => {
    return (
      this.fieldService.getFieldValueFromCrmObject(this.#activity(), SystemFieldIds.ActivityEffectiveDate)?.dateValue ??
      null
    );
  });

  effectResult = signal<EffectResult | undefined>(undefined);

  contentText = computed(() => {
    const effectResult = this.effectResult();

    const computedObjectType = this.objectType();
    if (computedObjectType === undefined) {
      return '';
    }

    if (this.recordChangeType() === 'IMPORTANT_FIELD_UPDATED') {
      if (effectResult?.changeLogSalesPerson === null && effectResult?.changeLogSalespersonId) {
        return `${this.salespersonUpdatedTranslation()} ${this.salespersonUnknownTranslation()}`.trim();
      }
      if (effectResult?.changeLogSalesPerson !== null) {
        const salespersonFullName = [
          effectResult?.changeLogSalesPerson.firstName ?? '',
          effectResult?.changeLogSalesPerson.lastName ?? '',
        ]
          .map((name) => name.trim())
          .join(' ');
        return `${this.salespersonUpdatedTranslation()} ${salespersonFullName}`.trim();
      }
      return `${this.salespersonNotAssignedTranslation()}`;
    }
    return '';
  });

  isUpdatedRecordChange = computed(() => {
    const effectResult = this.effectResult();
    return effectResult?.changeLog.crmChangeSubtype !== '';
  });

  constructor(
    private readonly fieldService: CrmFieldService,
    private readonly translateForCrmObjectService: TranslateForCrmObjectService,
    private readonly changeLogService: CRMChangeLogApiService,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    @Inject(SalespersonService) private readonly salespersonService: SalesSdk,
  ) {
    effect(() => {
      if (!this.recordChangeType() || this.recordChangeType() !== 'IMPORTANT_FIELD_UPDATED') {
        this.effectResult.set(undefined);
        this.isReady.set(true);
      } else if (this.recordChangeType() === 'IMPORTANT_FIELD_UPDATED') {
        const companyId =
          this.#activity().associations?.find((association) => association.crmObjectType === 'Company')?.crmObjectId ??
          '';
        const changeId =
          this.fieldService.getFieldValueFromCrmObject(this.#activity(), StandardIds.RecordChangeReferenceFieldID)
            ?.stringValue ?? '';

        this.changeLogService
          .getMultiChangeLog({
            crmObjectType: this.objectType(),
            namespace: this.namespace(),
            crmChangelogIdentifiers: [
              {
                crmObjectId: companyId,
                crmChangeId: changeId,
              },
            ],
          })
          .pipe(
            switchMap((res) => {
              const object = res.changeLogs[0];
              const salesPersonSubId = this.fieldService.getFieldValueFromCrmObject(
                object,
                StandardIds.CompanyPrimarySalespersonID,
              )?.stringValue;
              const createdByUserId = object.createdByUserId;
              const namespace = this.namespace() || '';

              return combineLatest([
                of(object),
                salesPersonSubId
                  ? this.salespersonService
                      .getSalespersonBySubjectId(namespace, salesPersonSubId)
                      .pipe(catchError(() => of(null)))
                  : of(null),
                createdByUserId ? this.config.services?.userService?.getMultiUsers([createdByUserId]) || EMPTY : EMPTY,
                of(salesPersonSubId),
              ]);
            }),
            take(1),
          )
          .subscribe(([object, salesPerson, simplifiedUser, salesPersonSubId]) => {
            const effectResult = {} as EffectResult;
            effectResult.changeLog = object;
            effectResult.changeLogSalesPerson = salesPerson;
            effectResult.changeLogUser = simplifiedUser;
            effectResult.changeLogSalespersonId = salesPersonSubId || null;
            this.effectResult.set(effectResult);
            this.isReady.set(true);
          });
      }
    });
  }
}
